/**
 * routePermissions.js — path ของ route → perm key ที่ต้องมีถึงจะเข้าได้
 *
 * ── หลักที่ใช้ตัดสินว่าหน้านี้ควรขอ key ตัวไหน ───────────────────────────
 * ด่านหน้าเว็บคุมระดับ "พื้นที่" ไม่ใช่ระดับ "การกระทำ"
 *
 *   หน้าเว็บ  ถาม "คุณควรเห็นพื้นที่นี้ไหม"      → ใช้ key ฝั่ง view เป็นหลัก
 *   API      ถาม "คุณทำสิ่งนี้ได้ไหม"          → ด่านเดิม 127 จุดยังคุมอยู่
 *
 * เหตุผล: ถ้าหน้าแก้ไขขอ key ฝั่งเขียนทั้งหมด คนที่วันนี้เปิดดูได้จะโดนเด้ง
 * ออกทันทีที่ปล่อยของ ทั้งที่ความเสียหายจริงถูกกันไว้ที่ API อยู่แล้ว การกัน
 * ซ้ำที่หน้าเว็บได้ความปลอดภัยเพิ่มน้อยมาก แต่แลกด้วยความเสี่ยงล็อกคนออกสูง
 *
 * หน้าที่โชว์ตัวเลขราคา (ใบเสนอราคา ใบแจ้งหนี้ ใบกำกับ) เป็นข้อยกเว้น —
 * พวกนี้ขอ key ฝั่งเงินตรง ๆ เพราะตัวหน้าเองคือข้อมูลที่ต้องกัน
 *
 * ── perms เป็น OR ────────────────────────────────────────────────────────
 * มี key ตัวใดตัวหนึ่งก็ผ่าน ใช้กฎเดียวกับ requirePermission ฝั่ง backend
 *
 * ── perms: null คืออะไร ──────────────────────────────────────────────────
 * "ล็อกอินแล้วเข้าได้ ไม่ต้องมี key" ใช้กับหน้าที่ทุกคนต้องเข้าได้จริง ๆ
 * เช่น /dashboard — ไม่ใช่ช่องโหว่ แต่เป็นการบอกว่าเราตั้งใจไม่กัน
 *
 * key ทุกตัวในไฟล์นี้ต้องมีอยู่จริงใน tbl_permissions (ดู migrations
 * 011_seed_permissions.sql และ 027_fix_permission_catalog.sql) พิมพ์ผิด
 * = ทุกคนที่ไม่ใช่แอดมินโดนบล็อกหน้านั้นถาวร
 */

import { matchPath } from "react-router-dom";

/**
 * [pattern, perms] — pattern เขียนแบบ react-router (`:id` ได้)
 * ลำดับในลิสต์ไม่สำคัญ ตัวจับคู่เลือก "อันที่เจาะจงที่สุด" ให้เอง
 */
export const ROUTE_PERMISSIONS = [
  // ── เครื่องมือ BOM / เอกสาร (เดิมอยู่นอกด่านทั้งหมด) ────────────────────
  // เลิกใช้แล้ว เหลือหน้าแจ้งย้าย — ล็อกอินแล้วเข้าได้ ลิงก์เก่าต้องไม่เจอหน้า "ไม่มีสิทธิ์"
  ["/tools/bom-management", null],
  // ระบบจัดซื้อ (migration 069) — /tools/bom2buy เป็นใบขอซื้อแบบใหม่ ของเดิม (iframe) ย้ายไป /legacy
  ["/tools/bom2buy", ["bom.to_buy", "purchase.view", "purchase.commit", "stock.receive"]],
  ["/tools/bom2buy/:id", ["bom.to_buy", "purchase.view", "purchase.commit", "stock.receive"]],
  ["/tools/bom2buy/legacy", ["bom.to_buy"]],
  ["/tools/bom2buy/history", ["bom.to_buy"]],
  ["/production/compare", ["bom.check_stock"]],
  ["/purchasing/approvals", ["purchase.approve", "purchase.pay"]],
  ["/store/goods-check", ["stock.receive", "purchase.commit", "bom.to_buy"]],
  ["/store/goods-check/new/:poId", ["stock.receive"]],
  ["/store/goods-check/:id", ["stock.receive", "purchase.commit", "bom.to_buy"]],
  ["/store/new-part/:poItemId", ["stock.receive"]],
  ["/purchasing/orders", ["bom.to_buy", "purchase.view", "purchase.commit", "stock.receive"]],
  ["/purchasing/orders/:id", ["bom.to_buy", "purchase.view", "purchase.commit", "stock.receive"]],
  ["/purchasing/orders/:id/print", ["bom.to_buy", "purchase.view", "purchase.commit", "stock.receive"]],
  ["/tools/bom-verify", ["bom.verify"]],
  ["/tools/kicad-coworker", ["kicad.request_update"]],
  ["/tools/kicad-relib/:board", ["kicad.request_update"]],
  ["/tools/filter-export", ["bom.export"]],
  ["/tools/comparator", ["bom.compare"]],
  ["/tools/documents", ["invoice.view"]],
  ["/tools/tax-invoices", ["invoice.view"]],
  ["/tools/project-generator", ["project.generate"]],
  ["/tools/flasher", ["tool.firmware_flash"]],
  ["/tools/flasher/manage", ["tool.firmware_flash"]],
  ["/tools/flasher/studio", ["tool.firmware_flash"]],
  ["/tools/flasher/:projectId", ["tool.firmware_flash"]],
  ["/tools/printers", ["tool.printer_view"]],
  ["/production/flasher", ["tool.firmware_flash"]],
  ["/production/flasher/manage", ["tool.firmware_flash"]],
  ["/production/flasher/studio", ["tool.firmware_flash"]],
  ["/production/flasher/:projectId", ["tool.firmware_flash"]],
  ["/admin/orderpcbeditlist", ["pcb.edit_order"]],
  ["/production/tools", ["production.tool_view", "production.tool_manage", "production.tool_inspect"]],
  ["/production/runs", ["stock.request", "stock.issue"]],
  ["/production/runs/:id", ["stock.request", "stock.issue"]],
  ["/production/runs/:id/report", ["stock.request", "stock.issue"]],
  ["/production/runs/:id/test/report", ["stock.request", "stock.issue", "qc.test"]],
  ["/production/runs/:id/prototype", ["bom.check_stock"]],
  // สรุปของที่เบิกเป็น BOM — คนเปิดรอบ/เบิกของ (หลังบ้าน canCreate = stock.request)
  ["/production/runs/:id/to-bom", ["stock.request"]],
  // คลังโปรเจกต์ — ทุกสายที่เกี่ยวกับงาน (เบิก/จ่ายของ · BOM · จัดซื้อ · สร้างโปรเจกต์) สิทธิ์เดียวกับ /api/project-hub
  ["/projects", ["stock.request", "stock.issue", "bom.check_stock", "bom.to_buy", "purchase.view", "purchase.commit", "project.generate"]],
  ["/projects/tracking", ["project.tracking"]], // ต้องอยู่ก่อน /projects/:code (ตัวแรกที่ตรงชนะ)
  ["/projects/:code", ["stock.request", "stock.issue", "bom.check_stock", "bom.to_buy", "purchase.view", "purchase.commit", "project.generate"]],
  // เช็คคลัง + ทำใบขอซื้อ + เปิดรอบผลิต — ต้องมีสิทธิ์ขอซื้อหรือขอเบิกอย่างใดอย่างหนึ่ง (ปุ่มที่ไม่มีสิทธิ์ API จะปฏิเสธเอง)
  ["/projects/:code/next-run/:version", ["stock.request", "bom.to_buy", "purchase.commit"]],
  ["/projects/:code/work", ["project.generate", "task.assign"]],
  ["/projects/legacy-link", ["stock.request"]],
  ["/production/boms/:id", ["bom.check_stock"]],
  ["/production/boms/:id/print", ["bom.check_stock"]],
  ["/production/boms", ["bom.check_stock"]],
  ["/production/customer-receipts/:id", ["bom.check_stock", "bom.manage", "stock.receive", "stock.request"]],
  ["/production/customer-receipts", ["bom.check_stock", "bom.manage", "stock.receive", "stock.request"]],
  ["/store/customer-receipt-matches/:id", ["bom.check_stock", "bom.manage", "stock.receive", "stock.request"]],
  ["/store/customer-receipt-matches", ["bom.check_stock", "bom.manage", "stock.receive", "stock.request"]],
  ["/sales/withdrawals", ["stock.request", "stock.issue"]],
  ["/store/suppliers", ["supplier.view", "supplier.manage", "supplier.spend"]],
  ["/store/supplier/:id/overview", ["supplier.view", "supplier.manage", "supplier.spend"]],

  // ── ผู้ใช้ที่ล็อกอินแล้ว ───────────────────────────────────────────────
  // แชทภายใน — ทุกคนที่ล็อกอินใช้ได้ สิทธิ์จริงคุมที่ "เป็นสมาชิกห้องไหน"
  ["/chat", null],
  ["/chat/:roomId", null],
  ["/dashboard", null],
  ["/admin/dashboard", null],
  // ของตัวเอง — API หาพนักงานจากบัญชีที่ล็อกอิน เห็นได้แค่ของตัวเอง
  ["/my-tools", null],
  ["/profile", ["profile.view"]],
  // ทุกคนที่ล็อกอินเปลี่ยนรหัสตัวเองได้ · ต้องเข้าได้ตอนถูกบังคับเปลี่ยนรหัส (migration 130)
  ["/change-password", null],
  ["/my-tasks", null],
  ["/tasks", null],
  ["/tasks/new", null],
  ["/tasks/templates", ["task.template"]],
  ["/tasks/:id", null],
  ["/admin/blogs", ["blog.manage"]],
  ["/admin/jobs/:id", ["quotation.view", "quotation.create"]],

  // ── แผนการผลิต ────────────────────────────────────────────────────────

  // ── สายงานใบเสนอราคา (qflow) ──────────────────────────────────────────
  // หน้ารายละเอียดใบเสนอราคาโชว์ราคา จึงขอ key ฝั่งเงินเพิ่มอีกทาง
  // ⚠ เดิมขอ qflow.board / qflow.intake / job.view ซึ่งไม่มีในระบบ — บล็อกจริงแล้วจะเข้าได้แค่แอดมิน (check-route-registry.js เจอ 20 ต.ค. 69)
  ["/admin/quotation-flow", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/intake/new", ["quotation.create"]],
  ["/admin/quotation-flow/intake/:jobId", ["quotation.create"]],
  ["/admin/quotation-flow/bom/:jobId", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/calc/:jobId", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/quotations", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/quotations/:id", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/production/:jobId", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/production-multi/:jobId", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/job/:id", ["quotation.view", "quotation.create"]],

  // ── แม่แบบและรายงาน ───────────────────────────────────────────────────

  // ── คลัง: ดู / เบิก (พนักงานทั่วไปใช้) ─────────────────────────────────
  ["/components", ["stock.view"]],
  ["/componentcheckboom", ["bom.check_stock"]],
  ["/componentcartlist", ["stock.request"]],
  ["/componentuserissuelist", ["stock.withdraw"]],
  ["/componentuserissuelist/:id", ["stock.withdraw"]],
  ["/componentuserrequestedimportancelist", ["stock.request"]],
  ["/componentuserrequestedcancellist", ["stock.request"]],

  // ── คลัง: แคตตาล็อกสินค้า ─────────────────────────────────────────────
  ["/componenteditlist/set", ["stock.edit"]],
  ["/componenteditlist/:id", ["stock.edit"]],
  ["/componenteditlist/:id/edit", ["stock.edit"]],
  ["/componentaddcartlist", ["stock.edit"]],

  // ── คลัง: รับเข้า / จ่ายออก / ใบขอเบิก ────────────────────────────────
  ["/componentrequestlist", ["stock.issue"]],
  ["/componentrequestlist/:id", ["stock.issue"]],
  ["/componentreceivelist", ["stock.receive"]],
  ["/componentreceivelist/:id", ["stock.receive"]],
  ["/componentissuelist", ["stock.issue"]],
  ["/componentissuelist/:id", ["stock.issue"]],
  ["/admin/componentrequestimportancelist", ["stock.issue"]],
  ["/admin/componentrequestedcancellist", ["stock.issue"]],

  // ── คลัง: ตั้งค่า (หมวดหมู่ ฟุตพรินต์ ผู้ผลิต ซัปพลายเออร์) ───────────
  ["/componentdefaultlist", ["stock.settings"]],
  ["/components/sync", ["stock.settings"]],
  ["/componentcreatecategory", ["stock.settings"]],
  ["/componentcreatesubcategory", ["stock.settings"]],
  ["/componentcreatefootprint", ["stock.settings"]],
  ["/componentcreatemanufacturing", ["stock.settings"]],
  ["/componentcreatesupplier", ["stock.settings"]],
  ["/stock/category/:id/edit", ["stock.settings"]],
  ["/stock/subcategory/:id/edit", ["stock.settings"]],
  ["/stock/footprint/:id/edit", ["stock.settings"]],
  ["/stock/manufacture/:id/edit", ["stock.settings"]],
  ["/stock/supplier/:id/edit", ["stock.settings"]],

  // ── ลูกค้า ────────────────────────────────────────────────────────────
  ["/admin/customers", ["customer.view"]],
  ["/admin/customer/:id", ["customer.view"]],
  ["/admin/customer/:id/overview", ["customer.view"]],
  ["/admin/customer/:id/edit", ["customer.view"]],
  ["/admin/customer/:id/quote/new", ["quotation.create"]],
  ["/admin/customer/:id/invoice/new", ["invoice.create"]],
  ["/admin/customer/:id/taxinvoice/new", ["invoice.create"]],

  // ── ใบเสนอราคา / งาน ──────────────────────────────────────────────────
  ["/admin/quotes", ["quotation.view", "quotation.create"]],
  ["/admin/quotes/new", ["quotation.create"]],
  ["/admin/quotes/:id/edit", ["quotation.create"]],
  ["/admin/quotations", ["quotation.view"]],
  ["/admin/quotation-documents", ["quotation.view"]],
  ["/admin/quotes-new/:id", ["quotation.view"]],
  ["/admin/quotes-new/:id/edit", ["quotation.create"]],
  ["/admin/quotations/create-from-pcb/:id", ["quotation.create"]],

  // ── ใบแจ้งหนี้ / ใบกำกับภาษี / ระบบบัญชี ─────────────────────────────
  ["/accounting", ["invoice.view", "purchase.pay", "purchase.approve", "stock.valuation"]],
  ["/accounting/sales/:invoiceNo/payments", ["invoice.view", "invoice.create", "purchase.pay", "purchase.approve"]],
  ["/admin/invoices", ["invoice.view"]],
  ["/admin/document-hub", ["invoice.view"]],
  ["/admin/invoice/:id", ["invoice.view"]],
  ["/admin/invoice/:id/edit", ["invoice.create"]],
  ["/admin/invoices-new/:id/edit", ["invoice.view"]],
  ["/admin/invoices/create-from-pcb/:id", ["invoice.create"]],
  ["/admin/taxinvoices", ["invoice.view"]],
  ["/admin/taxinvoice/:id", ["invoice.view"]],
  ["/admin/taxinvoice/:id/edit", ["invoice.create"]],
  ["/admin/invoice-submissions", ["invoice.export"]],

  // ── PCB ───────────────────────────────────────────────────────────────
  ["/admin/pcb-submissions", ["pcb.submissions"]],

  // ── ผู้ดูแลระบบ ───────────────────────────────────────────────────────
  ["/admin/rbac", ["rbac.manage"]],
  ["/admin/rbac/legacy", ["rbac.manage"]],
  ["/admin/rbac/people/:id", ["rbac.manage"]],
  ["/admin/rbac/departments/:id", ["rbac.manage"]],
  ["/admin/org", ["rbac.manage", "employee.view"]],
  ["/admin/org/people/:key", ["rbac.manage", "employee.view"]],
  ["/admin/org/:id", ["rbac.manage"]],
  ["/admin/security", ["security.view"]],
  // หน้าสถานะเครื่องเดิม → แท็บใน /admin/security (22 ต.ค. 69)
  ["/admin/system", null],
  ["/admin/permissions", ["rbac.manage"]],
  ["/admin/departments", ["rbac.manage"]],
  ["/admin/userlist", ["rbac.manage"]],
  ["/admin/user/:id/edit", ["user.manage"]],
  ["/admin/bank-accounts", ["bank.manage"]],
  ["/admin/signatures", ["signature.manage"]],
  // ── ลงทะเบียนครบทุกหน้า (20 ต.ค. 69 · ระบบบทบาท migration 129) ─────────────────
  // เดิม 49 หน้าไม่ได้ลงทะเบียน = ใครล็อกอินก็เปิดได้ · ตอนนี้ scripts/check-route-registry.js
  // ไม่ยอมให้ส่งขึ้นเว็บถ้ามีหน้าใน index.js ที่ไม่อยู่ในไฟล์นี้ (หรือใน PUBLIC_ROUTES)
  // QC (qc.test) — เข้าหน้าตรวจ/รายงาน/หน้ารวมงานตรวจ โดยไม่ต้องมีสิทธิ์เบิก-จ่ายของ
  ["/production/runs/:id/test", ["stock.request", "stock.issue", "qc.test"]],
  ["/production/qc", ["stock.request", "stock.issue", "qc.test"]],
  ["/production/phone-storage", ["production.phone_view", "production.phone_manage"]],
  ["/admin/employees", ["employee.view", "employee.manage"]],
  ["/admin/payroll", ["payroll.view_all", "payroll.manage"]],
  ["/admin/payroll/:employeeId", ["payroll.view_all", "payroll.manage"]],
  ["/my-payslip", null],
  ["/store/valuation", ["stock.valuation"]],
  ["/tools/price-estimate", ["estimate.view", "estimate.manage"]],
  ["/tools/price-estimate/:id", ["estimate.view", "estimate.manage"]],
  ["/tools/price-estimate/:id/edit", ["estimate.manage"]],
  ["/tools/price-estimate/settings", ["estimate.settings"]],
  ["/purchasing/customs", ["purchase.view", "purchase.commit"]],
  ["/purchasing/orders/:id/customs", ["purchase.view", "purchase.commit"]],
  ["/purchasing/orders/:id/customs/print", ["purchase.view", "purchase.commit"]],
  ["/purchasing/hs-codes", ["purchase.view", "purchase.commit"]],
  ["/purchasing/hs-codes/types", ["purchase.view", "purchase.commit"]],
  // อัปเดต EPN เลิกใช้ 22 ต.ค. 69 — ลิงก์เดิมพาไปหน้าคลัง BOM
  ["/tools/stock-to-csv", null],
  ["/tools/stock-to-csv/history", null],
  ["/tools/kicad-updates", ["kicad.request_update", "kicad.approve_update"]],
  ["/admin/quotation-flow/board", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/templates", ["quotation.view", "quotation.create"]],
  ["/admin/quotation-flow/reports", ["quotation.view", "quotation.create"]],
  ["/bom-projects", ["bom.check_stock", "bom.manage"]],
  ["/bom-projects/:id", ["bom.check_stock", "bom.manage"]],
  ["/componentissuelist/relink", ["stock.issue_relink"]],
  // หน้าที่แค่พาไปหน้าใหม่ (Navigate) — ล็อกอินแล้วเข้าได้ ลิงก์เก่าต้องไม่เจอ "ไม่มีสิทธิ์"
  ["/tools/calculator", null],
  ["/componenteditlist", null],
  ["/componentaddproductlist", null],
  ["/admin/workflows", null],
  ["/admin/workflows/create", null],
  ["/admin/workflows/:id", null],
  ["/admin/workflows/:id/assignments", null],
  ["/admin/templates", null],
  ["/admin/templates/create", null],
  ["/admin/dept-templates", null],
  ["/admin/reports", null],
  ["/admin/pm", null],
];

/**
 * หน้าที่ไม่ผ่านด่าน RequirePermission เลย (อยู่นอกกลุ่ม route ที่ต้องล็อกอิน) — ลูกค้าสั่ง PCB · ติดตามพัสดุ · ล็อกอิน
 * scripts/check-route-registry.js นับว่า "ลงทะเบียนแล้ว"
 */
export const PUBLIC_ROUTES = [
  "/", "/login", "*", "/track/:token", "/blogpost/:id",
  "/pcb-assembly-form", "/assemblypcb", "/custompcb", "/copypcb", "/orderpcb",
  "/mini/run-tests",
];

/**
 * ให้คะแนนความเจาะจงของ pattern — ยิ่งมีส่วนที่เขียนตายเยอะยิ่งชนะ
 *
 * ต้องมีเพราะ "/componenteditlist/set" กับ "/componenteditlist/:id" จับคู่กับ
 * /componenteditlist/set ได้ทั้งคู่ ถ้าเลือกอันแรกที่เจอตามลำดับในลิสต์
 * การสลับบรรทัดจะเปลี่ยนสิทธิ์ของหน้าโดยไม่มีใครรู้ตัว
 */
function specificity(pattern) {
  const segments = pattern.split("/").filter(Boolean);
  const staticSegments = segments.filter((s) => !s.startsWith(":")).length;
  return staticSegments * 100 + segments.length;
}

const SORTED = [...ROUTE_PERMISSIONS].sort(
  (a, b) => specificity(b[0]) - specificity(a[0]),
);

/**
 * หา perm ที่ path นี้ต้องใช้
 *
 * @param {string} pathname เช่น "/admin/customer/12/edit"
 * @returns {{ perms: string[]|null, pattern: string }|null}
 *          null = ไม่มีในตาราง (route สาธารณะ หรือยังไม่ได้ลงทะเบียน)
 */
export function permsForPath(pathname) {
  for (const [pattern, perms] of SORTED) {
    if (matchPath({ path: pattern, end: true }, pathname)) {
      return { perms, pattern };
    }
  }
  return null;
}
